import { css } from '@emotion/react'
import {
  Button,
  Cell,
  FormControl,
  Grid,
  HFlow,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Theme,
  Tooltip,
  useLocale,
  useTheme,
  VFlow,
} from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { RadioField, TextField } from 'components/form'
import { CiapCidSelectField } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { GrupoCondicaoSelectField } from 'components/form/field/select/GrupoCondicaoSelectField'
import { confirm } from 'components/modals/confirm'
import { getAggregators, getKeyNotDependentAggregators } from 'components/pivot-table/components/aggregator/utils'
import createDecorator from 'final-form-calculate'
import {
  ModeloPersonalizadoRelatorioGerencialDocument,
  ModelosByTipoDocument,
  useEditarModeloPersonalizadoRelatorioGerencialMutation,
  useExcluirModeloPersonalizadoRelatorioGerencialMutation,
  useSalvarModeloPersonalizadoRelatorioGerencialMutation,
} from 'graphql/hooks.generated'
import {
  AggregatorRelatorioGerencialEnum,
  ModeloPersonalizadoRelatorioGerencial,
  OpcaoSelecionadaRelatorioGerencialEnum,
  TipoAgrupamentoRelatorioGerencialEnum,
  TipoModuloEnum,
  UnidadePeriodoRelatorioGerencialEnum,
} from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { Form, FormRenderProps } from 'react-final-form'
import Permissions from 'types/Permissions'
import { emptyArrayOf } from 'util/array'
import { metaPath } from 'util/metaPath'
import { isObjectDeepFalsy } from 'util/object'

import { KeyMapping } from '../../../common/keyMapping-relatorioGerencial'
import { TipoAgrupamentoRadioGroupField } from '../../form/TipoAgrupamentoRadioGroupField'
import { RelatorioGerencialPeriodoLabel } from '../../periodo/RelatorioGerencialPeriodoLabel'
import { RelatorioGerencialPeriodoRadioField } from '../../periodo/RelatorioGerencialPeriodoRadioField'
import { RelatorioGerencialUnidadePeriodoSelectField } from '../../periodo/RelatorioGerencialUnidadePeriodoSelectField'
import {
  isPublicoToVisibilidadeModeloPersonalizadoEnum,
  relatorioGerencialFormModelToJsonModel,
  visibilidadeModeloPersonalizadoEnumToIsPublico,
} from '../converter-modeloPersonalizadoRelatorioGerencial'
import { isCampoInconsistente, isCampoPrivado } from '../utils-modeloPersonalizado'
import { createModeloPersonalizadoRelatoriosGerenciaisPeriodoFormCalculations } from './calculator-modeloPersonalizadoRelatorioGerencial'
import {
  ModeloPersonalizadoRelatorioGerencialCampoModel,
  ModeloPersonalizadoRelatorioGerencialFormModel,
} from './model-modeloPersonalizadoRelatorioGerencialForm'
import { ModeloPersonalizadoRelatorioGerencialCamposForm } from './ModeloPersonalizadoRelatorioGerencialCamposForm'
import { modeloPersonalizadoRelatorioGerencialFormValidator } from './validator-modeloPersonalizadoRelatorioGerencial'
import { VisibilidadeModeloPersonalizadoRadioGroupField } from './VisibilidadeModeloPersonalizadoRadioGroupField'

const meta = metaPath<ModeloPersonalizadoRelatorioGerencialFormModel>()

interface ModeloPersonalizadoRelatorioGerencialFormProps {
  keyMapping: Map<string, KeyMapping>
  editValues?: ModeloPersonalizadoRelatorioGerencial
  tipoModulo: TipoModuloEnum
  tituloModal: string
  isCadastro: boolean
  hasFiltroPeriodo: boolean
  hasFiltroTipoAgrupamento: boolean
  handleCloseModal: () => void
  handleCloseModalAndSendMetrics: () => void
  setFormHasBeenTouched: (state: boolean) => void
}

export const ModeloPersonalizadoRelatorioGerencialForm = (props: ModeloPersonalizadoRelatorioGerencialFormProps) => {
  const {
    keyMapping,
    editValues,
    tipoModulo,
    tituloModal,
    isCadastro,
    hasFiltroPeriodo,
    hasFiltroTipoAgrupamento,
    handleCloseModal,
    handleCloseModalAndSendMetrics,
    setFormHasBeenTouched,
  } = props

  const locale = useLocale()
  const aggregators = getAggregators(locale.aggregators)
  const keyNotDependentAggregators = getKeyNotDependentAggregators(aggregators)

  const theme = useTheme()
  const styles = createStyles(theme)
  const { hasAuthorization, isGestorEstadual } = useSession()
  const handleException = useErrorHandler()

  const initialValues = useMemo<ModeloPersonalizadoRelatorioGerencialFormModel>(
    () => ({
      id: editValues?.id,
      nome: editValues?.nome,
      modulo: editValues?.tipoModulo ?? tipoModulo,
      opcaoSelecionadaModal: hasFiltroPeriodo
        ? editValues?.modelo.opcaoSelecionada ?? OpcaoSelecionadaRelatorioGerencialEnum.PRIMEIRA
        : undefined,
      unidadePeriodoModal: hasFiltroPeriodo
        ? editValues?.modelo.unidadePeriodo ?? UnidadePeriodoRelatorioGerencialEnum.MES
        : undefined,
      gruposCondicoes: editValues?.modelo.gruposCondicoes,
      ciapsCids: [...(editValues?.modelo.ciaps ?? []), ...(editValues?.modelo.cids ?? [])],
      campos: [
        ...(editValues?.modelo.campos.map((campo, index) => ({
          _id: index,
          key: campo.key,
          posicao: campo.posicao,
          items: campo.items,
          isInconsistente: isCampoInconsistente(campo.key, keyMapping, tipoModulo, isGestorEstadual, hasAuthorization),
        })) ?? emptyArrayOf<ModeloPersonalizadoRelatorioGerencialCampoModel>()),
      ],
      aggregatorModal: editValues?.modelo.aggregator ?? AggregatorRelatorioGerencialEnum.CONTAGEM,
      visibilidade: isPublicoToVisibilidadeModeloPersonalizadoEnum(editValues?.isPublico),
      tipoAgrupamentoModal: hasFiltroTipoAgrupamento
        ? editValues?.modelo.tipoAgrupamento ?? TipoAgrupamentoRelatorioGerencialEnum.PESSOAS
        : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tipoModulo]
  )

  const canWriteModeloPublico = hasAuthorization(Permissions.relatorios.gerenciais.cadastrarModelosPublicos)
  const hasCampoInconsistente = useMemo(() => initialValues.campos.some((campo) => campo.isInconsistente), [
    initialValues,
  ])
  const hasFiltroCiapCid = tipoModulo === TipoModuloEnum.RELATORIO_GERENCIAL_ATENDIMENTO

  const [salvarModeloPersonalizadoRelatorioGerencial] = useSalvarModeloPersonalizadoRelatorioGerencialMutation()
  const [editarModeloPersonalizadoRelatorioGerencial] = useEditarModeloPersonalizadoRelatorioGerencialMutation()
  const [excluirModeloPersonalizadoRelatorioGerencial] = useExcluirModeloPersonalizadoRelatorioGerencialMutation()

  const onSubmit = (values: ModeloPersonalizadoRelatorioGerencialFormModel) => {
    if (isCadastro) {
      salvarModeloPersonalizadoRelatorioGerencial({
        variables: {
          input: {
            isPublico: visibilidadeModeloPersonalizadoEnumToIsPublico(values.visibilidade),
            modeloPersonalizado: relatorioGerencialFormModelToJsonModel(values),
            nome: values.nome,
            tipoModulo: values.modulo,
          },
        },
        refetchQueries: [{ query: ModelosByTipoDocument, variables: { tipoModulo } }],
      })
        .then(handleCloseModal)
        .catch(handleException)
    } else {
      editarModeloPersonalizadoRelatorioGerencial({
        variables: {
          input: {
            id: values.id,
            modeloPersonalizado: relatorioGerencialFormModelToJsonModel(values),
            nome: values.nome,
            tipoModulo: values.modulo,
          },
        },
        refetchQueries: [
          { query: ModelosByTipoDocument, variables: { tipoModulo } },
          { query: ModeloPersonalizadoRelatorioGerencialDocument, variables: { id: values.id } },
        ],
      })
        .then(handleCloseModal)
        .catch(handleException)
    }
  }

  const handleExcluir = () => {
    return confirm({
      title: `Deseja excluir o modelo de relatório?`,
      type: 'danger',
      confirmLabel: 'Excluir',
      onConfirm: async () => {
        excluirModeloPersonalizadoRelatorioGerencial({
          variables: {
            input: {
              id: editValues?.id,
              tipoModulo,
            },
          },
          refetchQueries: [{ query: ModelosByTipoDocument, variables: { tipoModulo } }],
        })
        handleCloseModal()
      },
    })()
  }

  const decorators = useMemo(
    () => [
      createDecorator(
        ...createModeloPersonalizadoRelatoriosGerenciaisPeriodoFormCalculations(meta, keyMapping, isCadastro)
      ),
    ],
    [isCadastro, keyMapping]
  )

  const validator = useMemo(
    () =>
      modeloPersonalizadoRelatorioGerencialFormValidator({
        hasFiltroCiapCid: hasFiltroCiapCid,
        hasFiltroPeriodo: hasFiltroPeriodo,
        hasFiltroTipoAgrupamento: hasFiltroTipoAgrupamento,
        isModeloSalvoPublico: editValues?.isPublico,
      }),
    [editValues, hasFiltroCiapCid, hasFiltroPeriodo, hasFiltroTipoAgrupamento]
  )

  const renderForm = ({
    values,
    handleSubmit,
    errors,
    submitFailed,
    touched,
  }: FormRenderProps<ModeloPersonalizadoRelatorioGerencialFormModel>) => {
    const { campos, opcaoSelecionadaModal, unidadePeriodoModal } = values

    const isPublico = visibilidadeModeloPersonalizadoEnumToIsPublico(values.visibilidade)
    const hasCampoPrivado = campos.some(({ key }) => isCampoPrivado(keyMapping.get(key)))
    const hasCampoInvalido = isCadastro && isPublico && hasCampoPrivado

    setFormHasBeenTouched(!isObjectDeepFalsy(touched))

    return (
      <Fragment>
        <ModalHeader title={tituloModal} />
        <ModalBody>
          <VFlow>
            <Grid wrap>
              <Cell size={6}>
                <TextField name={meta.nome} label='Nome do modelo' required size={6} />
              </Cell>
              {canWriteModeloPublico && (
                <Cell size={6}>
                  <Tooltip text={!isCadastro && 'Não é possível editar a visibilidade'}>
                    <div>
                      <VisibilidadeModeloPersonalizadoRadioGroupField
                        name={meta.visibilidade}
                        label='Visibilidade'
                        required
                        disabled={!isCadastro}
                      />
                    </div>
                  </Tooltip>
                </Cell>
              )}
              <Cell size={12}>
                <VFlow>
                  {hasFiltroPeriodo && (
                    <VFlow>
                      <RelatorioGerencialPeriodoLabel />
                      <VFlow css={styles.periodoContainer}>
                        <Grid wrap alignItems='center'>
                          <Cell size={6}>
                            <RelatorioGerencialUnidadePeriodoSelectField
                              name={meta.unidadePeriodoModal}
                              style={styles.unidadePeriodoSelectField}
                            />
                          </Cell>
                          <Cell>
                            <RelatorioGerencialPeriodoRadioField
                              nameOpcaoSelecionada={meta.opcaoSelecionadaModal}
                              opcaoSelecionadaValue={opcaoSelecionadaModal}
                              unidadeTempoValue={unidadePeriodoModal}
                              isModal
                            />
                          </Cell>
                        </Grid>
                      </VFlow>
                    </VFlow>
                  )}
                  {hasFiltroTipoAgrupamento && <TipoAgrupamentoRadioGroupField name={meta.tipoAgrupamentoModal} />}
                  {hasFiltroCiapCid && (
                    <Grid alignItems='stretch' direction='row'>
                      <Cell size={6}>
                        <CiapCidSelectField
                          label='CIAP2 e CID10'
                          placeholder='Selecione outros CIAP2 e CID10'
                          name={meta.ciapsCids}
                          multiple
                        />
                      </Cell>
                      <Cell size={6}>
                        <GrupoCondicaoSelectField
                          label='Grupos de condições prioritários'
                          placeholder='Selecione grupos de condições prioritários'
                          name={meta.gruposCondicoes}
                          multiple
                        />
                      </Cell>
                    </Grid>
                  )}
                  {hasFiltroPeriodo && <div style={styles.divider}></div>}
                  <ModeloPersonalizadoRelatorioGerencialCamposForm
                    keyMapping={keyMapping}
                    meta={meta.campos}
                    campos={campos}
                    isPublico={isPublico}
                    errors={submitFailed && errors}
                    modulo={tipoModulo}
                    hasCampoInconsistente={hasCampoInconsistente}
                    hasCampoInvalido={hasCampoInvalido}
                    setFormHasBeenTouched={setFormHasBeenTouched}
                  />
                  <FormControl label='Forma de apresentação' required error={errors.aggregatorModal}>
                    <HFlow>
                      {keyNotDependentAggregators.map((f) => (
                        <RadioField name={meta.aggregatorModal} label={f.label} value={f.id} key={'modal_' + f.label} />
                      ))}
                    </HFlow>
                  </FormControl>
                </VFlow>
              </Cell>
            </Grid>
          </VFlow>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='space-between'>
            {!isCadastro ? (
              <Button kind='danger' hidden={isCadastro} onClick={handleExcluir}>
                Excluir modelo
              </Button>
            ) : (
              <div></div>
            )}
            <HFlow justifyContent='flex-end'>
              <Button kind='normal' onClick={handleCloseModalAndSendMetrics}>
                Cancelar
              </Button>
              <Tooltip text={hasCampoInvalido && 'Exclua os campos privados'}>
                <Button kind='primary' onClick={handleSubmit} disabled={hasCampoInvalido}>
                  Salvar
                </Button>
              </Tooltip>
            </HFlow>
          </HFlow>
        </ModalFooter>
      </Fragment>
    )
  }
  return (
    <Form<ModeloPersonalizadoRelatorioGerencialFormModel>
      render={renderForm}
      onSubmit={onSubmit}
      initialValues={initialValues}
      decorators={decorators}
      validate={validator}
    />
  )
}

const createStyles = (theme: Theme) => ({
  periodoContainer: css`
    position: relative;
    padding: 1rem;
    border: 1px solid ${theme.pallete.gray.c80};
  `,

  unidadePeriodoSelectField: css`
    position: absolute;
    margin-left: -0.5rem;
    top: -0.7rem;
    padding: 0 0.5rem;
    background-color: ${theme.pallete.surface.main};
  `,

  divider: {
    borderBottom: `1px solid ${theme.pallete.divider}`,
  },
})
